import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users, Mail, Calendar, MessageSquare,
  BarChart3, Settings, LogOut, RefreshCw, Heart,
  FileText, CalendarDays, Ticket, Image, ShoppingBag, Star, Clock
} from 'lucide-react';
import { getRandomAffirmation } from '@/data/affirmations';
import { apiFetch, clearSession } from '@/lib/api';

import DashboardTab from './file-room/DashboardTab';
import ContactsTab from './file-room/ContactsTab';
import RegistrationsTab from './file-room/RegistrationsTab';
import NewsletterSubscribersTab from './file-room/NewsletterSubscribersTab';
import TicketSalesTab from './file-room/TicketSalesTab';
import CalendarTab from './file-room/CalendarTab';
import NewsletterPostsTab from './file-room/NewsletterPostsTab';
import TeamTab from './file-room/TeamTab';
import SettingsTab from './file-room/SettingsTab';
import GalleryTab from './file-room/GalleryTab';
import StoreTab from './file-room/StoreTab';
import SponsorTab from './file-room/SponsorTab';
import ScheduleTab from './file-room/ScheduleTab';
import AIAssistant from '@/components/AIAssistant';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FileRoomPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [refreshKey, setRefreshKey] = useState(0);

  const checkAuth = useCallback(async () => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      return;
    }
    try {
      const response = await apiFetch(`${API}/auth/me`);
      if (!response.ok) {
        clearSession();
        setIsAuthenticated(false);
        navigate('/file-room/login');
        return;
      }
      const userData = await response.json();
      setUser(userData);
      setIsAuthenticated(true);
    } catch {
      clearSession();
      setIsAuthenticated(false);
      navigate('/file-room/login');
    }
  }, [navigate, location.state]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogout = async () => {
    try {
      await apiFetch(`${API}/auth/logout`, { method: 'POST' });
    } catch {}
    clearSession();
    navigate('/file-room/login');
  };

  const handleRefresh = () => setRefreshKey(k => k + 1);

  const getTabs = () => {
    const tabs = [{ id: 'dashboard', name: 'Dashboard', icon: BarChart3 }];
    if (user?.role === 'admin' || user?.role === 'staff') {
      tabs.push(
        { id: 'contacts', name: 'Contacts', icon: MessageSquare },
        { id: 'registrations', name: 'Event Registrations', icon: Calendar },
        { id: 'newsletter', name: 'Newsletter', icon: Mail }
      );
    }
    if (user?.role === 'admin') {
      tabs.push(
        { id: 'tickets', name: 'Ticket Sales', icon: Ticket },
        { id: 'schedule', name: 'Schedule', icon: Clock },
        { id: 'store', name: 'Store', icon: ShoppingBag },
        { id: 'sponsors', name: 'Sponsors', icon: Star },
        { id: 'gallery', name: 'Gallery', icon: Image },
        { id: 'calendar', name: 'Calendar', icon: CalendarDays },
        { id: 'posts', name: 'Newsletter Posts', icon: FileText },
        { id: 'team', name: 'Team & Staff', icon: Users },
        { id: 'settings', name: 'Settings', icon: Settings }
      );
    }
    return tabs;
  };

  // Loading / unauthenticated states
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-center max-w-md mx-4">
          <div className="animate-pulse mb-8"><Heart className="w-12 h-12 text-brand-gold mx-auto" /></div>
          <p className="text-white text-xl italic mb-4">"{getRandomAffirmation()}"</p>
          <p className="text-gray-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return null;

  const tabs = getTabs();
  const canViewStaffTabs = user?.role === 'admin' || user?.role === 'staff';
  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-gray-100" data-testid="file-room-page">
      {/* Header */}
      <div className="bg-brand-dark py-6">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-white" data-testid="file-room-title">The File Room</h1>
              <p className="text-gray-400 text-sm mt-1">
                Welcome, {user?.name}
                <span className="ml-2 px-2 py-0.5 text-xs bg-brand-gold text-brand-dark rounded" data-testid="user-role-badge">{user?.role}</span>
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={handleRefresh} className="flex items-center gap-2 bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20" data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20" data-testid="logout-btn">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="tab-navigation">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'bg-brand-dark text-white' : 'bg-white text-brand-dark hover:bg-gray-50'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.name}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && <DashboardTab key={`dash-${refreshKey}`} user={user} />}
        {activeTab === 'contacts' && canViewStaffTabs && <ContactsTab key={`contacts-${refreshKey}`} user={user} />}
        {activeTab === 'registrations' && canViewStaffTabs && <RegistrationsTab key={`regs-${refreshKey}`} user={user} />}
        {activeTab === 'newsletter' && canViewStaffTabs && <NewsletterSubscribersTab key={`news-${refreshKey}`} user={user} />}
        {activeTab === 'tickets' && isAdmin && <TicketSalesTab key={`tickets-${refreshKey}`} />}
        {activeTab === 'store' && isAdmin && <StoreTab key={`store-${refreshKey}`} />}
        {activeTab === 'schedule' && isAdmin && <ScheduleTab key={`schedule-${refreshKey}`} />}
        {activeTab === 'sponsors' && isAdmin && <SponsorTab key={`sponsors-${refreshKey}`} />}
        {activeTab === 'gallery' && isAdmin && <GalleryTab key={`gallery-${refreshKey}`} />}
        {activeTab === 'calendar' && isAdmin && <CalendarTab key={`cal-${refreshKey}`} />}
        {activeTab === 'posts' && isAdmin && <NewsletterPostsTab key={`posts-${refreshKey}`} />}
        {activeTab === 'team' && isAdmin && <TeamTab key={`team-${refreshKey}`} />}
        {activeTab === 'settings' && isAdmin && <SettingsTab key={`settings-${refreshKey}`} />}
      </div>

      {/* AI Assistant - available across all tabs */}
      {isAdmin && <AIAssistant />}
    </div>
  );
};

export default FileRoomPage;
